// ========================================
// Professors View - Professor Management
// ========================================

const ProfessorsView = {
    // State
    searchTerm: '',
    // The list shows ten people per page.
    page: 1,
    pageSize: 10,
    sortState: {
        column: 'name',
        direction: 'asc'
    },

    toggleSort(column) {
        if (this.sortState.column === column) {
            this.sortState.direction = this.sortState.direction === 'asc' ? 'desc' : 'asc';
        } else {
            this.sortState.column = column;
            this.sortState.direction = 'asc';
        }
        this.page = 1;
        this.updateTable();
    },

    renderSortableHeader(label, column) {
        const isActive = this.sortState.column === column;
        const icon = isActive
            ? `<i class="fas fa-sort-${this.sortState.direction === 'asc' ? 'up' : 'down'}" style="margin-left: 5px;"></i>`
            : '<i class="fas fa-sort" style="margin-left: 5px; opacity: 0.3;"></i>';
        return `<th ${UIEvents.attr("click", function(event,captured){ProfessorsView.toggleSort(String(captured[0]));}, [column])} style="cursor: pointer; user-select: none;">${label}${icon}</th>`;
    },

    // 'active' > 'upcoming' > 'inactive', para poder ordenar la columna ACTIVO
    getProfessorActiveState(professor) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        if (!professor.hireDate) return { state: 'inactive', text: 'NO' };

        const hire = new Date(professor.hireDate);
        hire.setHours(0, 0, 0, 0);

        // «Próximo» solo en los 15 días antes del alta, como la lista «Próximas altas»;
        // antes de eso todavía no está en activo.
        if (today < hire) {
            const daysLeft = Math.round((hire - today) / 86400000);
            return daysLeft <= 15 ? { state: 'upcoming', text: 'PRÓXIMO' } : { state: 'inactive', text: 'NO' };
        }

        if (professor.terminationDate) {
            const term = new Date(professor.terminationDate);
            term.setHours(0, 0, 0, 0);
            if (today <= term) return { state: 'active', text: 'SI' };
            return { state: 'inactive', text: 'NO' };
        }

        return { state: 'active', text: 'SI' };
    },

    // Cuentas de personal (admin/coordinación/RRHH/almacén) sin ficha de
    // profesor: no aparecen en ningún listado de personal hoy. Se muestran
    // como filas adicionales de esta misma tabla, con las columnas propias
    // de profesor en blanco. Solo admin/coordinación reciben professorId en
    // AuthManager.users (ver file-storage.js canReadUsers); para el resto
    // esa lista viene de /api/contacts sin professorId, así que no se puede
    // distinguir con fiabilidad y se omite.
    STAFF_ROLE_LABELS: { admin: 'Administración', coordinator: 'Coordinación', rrhh: 'RRHH', warehouse: 'Almacén' },

    getExtraStaffAccounts() {
        if (!AuthManager.isAdmin() && !AuthManager.isCoordinator()) return [];
        let accounts = (AuthManager.users || [])
            .filter(u => Object.hasOwn(this.STAFF_ROLE_LABELS, u.role) && !u.professorId)
            .map(u => ({
                id: 'user:' + u.id,
                isStaffAccount: true,
                staffUserId: u.id,
                staffUsername: u.username,
                staffRole: u.role,
                name: u.name,
                specialty: this.STAFF_ROLE_LABELS[u.role],
                photo: u.photo || null
            }));

        if (this.searchTerm) {
            const query = this.searchTerm.toLowerCase();
            accounts = accounts.filter(u =>
                (u.name || '').toLowerCase().includes(query) ||
                u.specialty.toLowerCase().includes(query)
            );
        }
        return accounts;
    },

    async deleteStaffAccount(userId, username, displayName) {
        if (!AuthManager.isAdmin()) return;
        const result = await SafeHTML.fire({
            title: '¿Eliminar cuenta?',
            text: `Se eliminará el acceso de ${displayName} (@${username}). No es una ficha de profesor, es solo su cuenta de acceso; esta acción no se puede deshacer.`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Eliminar',
            cancelButtonText: 'Cancelar',
            confirmButtonColor: '#ef4444',
            cancelButtonColor: '#64748b'
        });
        if (!result.isConfirmed) return;
        const deletion = await AuthManager.deleteUser(username);
        if (deletion.success) {
            Utils.showToast('Cuenta eliminada', 'success');
            this.updateTable();
        } else {
            Utils.showToast(deletion.message || 'No se pudo eliminar la cuenta', 'error');
        }
    },

    getProcessedProfessors(baseProfessors = null) {
        let professors = baseProfessors || DataManager.getProfessors() || [];

        // 1. Filter
        if (this.searchTerm) {
            const query = this.searchTerm.toLowerCase();
            professors = professors.filter(p => {
                const fName = p.firstName || (p.name ? p.name.split(' ')[0] : '');
                const lName = p.lastName || (p.name ? p.name.split(' ').slice(1).join(' ') : '');
                const specialty = p.specialty || '';

                return (
                    fName.toLowerCase().includes(query) ||
                    lName.toLowerCase().includes(query) ||
                    specialty.toLowerCase().includes(query)
                );
            });
        }

        // 2. Sort
        const dir = this.sortState.direction === 'asc' ? 1 : -1;
        professors.sort((a, b) => {
            let valA, valB;

            switch (this.sortState.column) {
                case 'name':
                    valA = (a.firstName || a.name || '').toLowerCase();
                    valB = (b.firstName || b.name || '').toLowerCase();
                    break;
                case 'rate':
                    // Hourly rates first, then fixed salaries: the units differ.
                    valA = StaffPay.isFixed(a) ? 1e7 + StaffPay.monthlySalary(a) : Number(a.hourlyRate) || 0;
                    valB = StaffPay.isFixed(b) ? 1e7 + StaffPay.monthlySalary(b) : Number(b.hourlyRate) || 0;
                    break;
                case 'hireDate':
                    valA = a.hireDate || '';
                    valB = b.hireDate || '';
                    break;
                case 'terminationDate':
                    valA = a.terminationDate || '';
                    valB = b.terminationDate || '';
                    break;
                case 'active': {
                    const rank = { active: 2, upcoming: 1, inactive: 0 };
                    valA = rank[this.getProfessorActiveState(a).state];
                    valB = rank[this.getProfessorActiveState(b).state];
                    break;
                }
                case 'hours':
                    valA = this.getProfessorHours(a.id);
                    valB = this.getProfessorHours(b.id);
                    break;
                case 'workload':
                    valA = this.calculateWorkload(this.getProfessorWeeklyHoursNow(a.id)).percentage;
                    valB = this.calculateWorkload(this.getProfessorWeeklyHoursNow(b.id)).percentage;
                    break;
                default:
                    return 0;
            }

            if (valA < valB) return -1 * dir;
            if (valA > valB) return 1 * dir;
            return 0;
        });

        return professors;
    },

    // ─── The list (design G): filters, figures, table and what is pending ───
    // situation: 'active' (contract in force) | 'upcoming' (hire in 15 days) | 'inactive' | 'all'.
    situation: 'all',
    payFilter: 'all',
    onlyPending: false,
    pendingKind: null,
    tab: 'list',
    _routeView: undefined,

    /** Whoever manages staff (contracts, advisory, pay): admin, RRHH or the personal-laboral module. */
    canManageStaff() {
        return typeof ProfessorProfile !== 'undefined' && ProfessorProfile.canManageStaff();
    },

    todayIso() {
        const d = new Date();
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    },

    addDays(iso, n) {
        const d = new Date(`${iso}T12:00:00`);
        d.setDate(d.getDate() + n);
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    },

    /** Pay type of a person for the filter and the table: hourly | fixed | freelance. */
    payKindOf(professor) {
        if (StaffPay.isFixed(professor)) return 'fixed';
        return StaffPay.isFreelance(professor) ? 'freelance' : 'hourly';
    },

    PAY_KIND_LABELS: { hourly: 'Por horas', fixed: 'Sueldo fijo', freelance: 'Autónomo' },

    /**
     * What is pending for a person, for RRHH: the hire not told to the advisory, no account, no
     * hourly rate, an IRPF not told, a fixed salary without its working schedule.
     */
    pendingOf(professor) {
        if (!this.canManageStaff() || professor.isStaffAccount) return [];
        const state = this.getProfessorActiveState(professor).state;
        const current = state === 'active' || state === 'upcoming';
        const flags = [];
        if (this.pendingAdvisoryNotices([professor]).length) flags.push('advisory');
        if (AuthManager.isAdmin() && current && !AuthManager.getUser(professor.id)) flags.push('access');
        if (current && !StaffPay.isFixed(professor) && !(Number(professor.hourlyRate) > 0)) flags.push('rate');
        if (current && StaffPay.isFixed(professor) && !professor.workSchedule?.regular) flags.push('schedule');
        return flags;
    },

    // Thin line icons of the design (pencil, key, more), drawn inline in the current colour.
    ICONS: {
        edit: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M17 20h4"/><path d="M14.5 4.5a2.1 2.1 0 0 1 3 3L8 17l-4 1 1-4z"/></svg>',
        key: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="8" cy="15" r="4"/><path d="m11 12 9-9M17 6l3 3"/></svg>',
        payroll: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="4" y="3" width="16" height="18" rx="2"/><path d="M8 8h8M8 12h8M8 16h5"/></svg>',
        upload: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z"/><path d="M14 3v5h5"/><path d="M12 17v-6"/><path d="m9.5 13.5 2.5-2.5 2.5 2.5"/></svg>',
        plus: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" aria-hidden="true"><path d="M12 5v14M5 12h14"/></svg>',
        more: '<svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><circle cx="5" cy="12" r="1.8"/><circle cx="12" cy="12" r="1.8"/><circle cx="19" cy="12" r="1.8"/></svg>'
    },

    PENDING: {
        advisory: { flag: 'Avisar asesoría', title: 'Altas sin avisar a la asesoría', warn: true },
        access: { flag: 'Sin acceso', title: 'Sin acceso a SICAF' },
        rate: { flag: 'Sin tarifa', title: 'Por horas sin tarifa', note: 'su coste estimado sale a cero' },
        schedule: { flag: 'Sin horario', title: 'Sueldo fijo sin horario', note: 'sin él no se reparte su coste por formación' }
    },

    /** The route (profesores, profesores/activos, profesores/altas, profesores/curso) sets the starting filter. */
    applyRoute(subView) {
        if (subView === this._routeView) return;
        this._routeView = subView;
        this.tab = subView === 'curso' ? 'course' : 'list';
        // The list opens on «Todas»: RRHH thought it was the full list while it showed «En alta» (2026-10-27).
        this.situation = subView === 'altas' ? 'upcoming' : subView === 'activos' ? 'active' : 'all';
        this.payFilter = 'all';
        this.onlyPending = false;
        this.pendingKind = null;
        this.page = 1;
    },

    /** Repaints only what a filter or an action changes (figures, situation or pay-type buttons,
     *  the list or «El curso» and the column beside it), keeping the header, the search box and
     *  the scroll as they are. */
    refresh() {
        const area = document.getElementById('content-area');
        const page = document.getElementById('professors-page');
        if (!area || !page) return;
        const fresh = document.createElement('div');
        SafeHTML.set(fresh, this.render(this._routeView));
        const parts = ['.pl-stats', '.pl-situations', '.pl-chip-slot', '.pl-table-card', '.pl-aside'];
        if (!page.querySelector('.pl-table-card') || !fresh.querySelector('.pl-table-card')) {
            SafeHTML.set(area, fresh.innerHTML);
            this.attachEventListeners();
            return;
        }
        for (const selector of parts) {
            const now = page.querySelector(selector), next = fresh.querySelector(selector);
            if (now && next) now.replaceWith(next);
        }
    },

    setFilter(key, value) {
        this[key] = value;
        if (key !== 'pendingKind') this.pendingKind = null;
        this.page = 1;
        this.refresh();
    },

    /** «Lista» ⇄ «El curso»: changes the address and repaints only what is under the header. */
    setTab(tab) {
        if (tab === this.tab) return;
        const route = tab === 'course' ? 'profesores/curso' : 'profesores';
        const page = document.getElementById('professors-page');
        if (!page?.querySelector('.pl-body')) {
            this._routeView = undefined;
            return App.navigate(route);
        }
        history.pushState(null, '', '/' + route);
        App.currentView = route;
        App.updateBreadcrumbs?.(route);
        const fresh = document.createElement('div');
        SafeHTML.set(fresh, this.render(tab === 'course' ? 'curso' : null));
        for (const selector of ['.pl-tabs', '.pl-body']) {
            const now = page.querySelector(selector), next = fresh.querySelector(selector);
            if (now && next) now.replaceWith(next);
        }
        this.attachEventListeners();
    },

    /** The people shown in the list after every filter (search, situation, pay type, pending). */
    listedProfessors(all) {
        const search = this.getProcessedProfessors(all);
        return search.filter(p => {
            const state = this.getProfessorActiveState(p).state;
            if (this.situation !== 'all' && state !== this.situation) return false;
            if (this.payFilter !== 'all' && this.payKindOf(p) !== this.payFilter) return false;
            const pending = this.pendingOf(p);
            if (this.pendingKind && !pending.includes(this.pendingKind)) return false;
            if (this.onlyPending && !pending.length) return false;
            return true;
        });
    },

    render(subView = null) {
        try {
            // Role restriction for professors
            if (AuthManager.isProfessor() && !AuthManager.hasModule('profesores')) {
                const user = AuthManager.getCurrentUser();
                if (user && user.professorId) {
                    App.navigate(`profesor/${user.professorId}`);
                    return '';
                }
            }
            this.applyRoute(subView);
            const manage = this.canManageStaff();
            if (this.tab === 'course' && !manage) this.tab = 'list';

            const allProfessors = DataManager.getProfessors() || [];
            const canSeeSalaries = (typeof AuthManager !== 'undefined') && AuthManager.isLoggedIn() ? AuthManager.canSeeSalaries() : true;
            const yearStart = (() => { const d = new Date(); return d.getMonth() >= 8 ? d.getFullYear() : d.getFullYear() - 1; })();
            const counts = { active: 0, upcoming: 0, inactive: 0, all: allProfessors.length };
            for (const p of allProfessors) counts[this.getProfessorActiveState(p).state]++;

            const header = `
                <div class="pl-head">
                    <div class="pl-head-text">
                        <span class="pn-mono-note">Personal y RRHH · Curso ${yearStart} / ${yearStart + 1}</span>
                        <h1 class="pl-title">Profesores</h1>
                    </div>
                    <div class="pl-head-actions">
                        ${manage ? `<nav class="pl-tabs" aria-label="Vistas de profesores">
                            <button type="button" class="pl-tab ${this.tab === 'list' ? 'is-active' : ''}" aria-pressed="${this.tab === 'list'}" ${UIEvents.attr("click", function(event,captured){ProfessorsView.setTab('list');}, [])}>Lista</button>
                            <button type="button" class="pl-tab ${this.tab === 'course' ? 'is-active' : ''}" aria-pressed="${this.tab === 'course'}" ${UIEvents.attr("click", function(event,captured){ProfessorsView.setTab('course');}, [])}>El curso</button>
                        </nav>` : ''}
                        ${(AuthManager.isAdmin() || AuthManager.isRRHH()) ? `<button type="button" class="pl-head-btn" ${UIEvents.attr("click", function(event,captured){App.navigate('nominas');}, [])}>${this.ICONS.payroll} Nóminas</button>` : ''}
                        ${typeof ContractDocs !== 'undefined' && ContractDocs.canManage() ? `<button type="button" class="pl-head-btn" title="Uno o varios PDF: cada contrato va a la ficha de su persona por el DNI" ${UIEvents.attr("click", function(event,captured){ContractDocs.pick();}, [])}>${this.ICONS.upload} Subir contratos</button>` : ''}
                        ${AuthManager.canEditProfessorDirectory() ? `<button type="button" class="pl-head-btn is-primary" ${UIEvents.attr("click", function(event,captured){ProfessorsView.openNewProfessorModal();}, [])}>${this.ICONS.plus} Nueva persona</button>` : ''}
                    </div>
                </div>`;

            if (this.tab === 'course' && typeof ProfessorsCourse !== 'undefined') {
                return `<div class="view-container dnr-proposal pn-stack" id="professors-page">${header}<div class="pl-body pn-stack">${ProfessorsCourse.render(allProfessors, yearStart)}</div></div>`;
            }

            // Figures: today's staff, the next hires, pay types and the course's hours.
            const activeProfs = allProfessors.filter(p => this.getProfessorActiveState(p).state === 'active');
            const upcoming = allProfessors.filter(p => this.getProfessorActiveState(p).state === 'upcoming');
            const upcomingUntold = this.pendingAdvisoryNotices(upcoming).length;
            const kinds = { hourly: 0, fixed: 0, freelance: 0 };
            for (const p of activeProfs) kinds[this.payKindOf(p)]++;
            const totalHours = this.calculateTotalHours(allProfessors);
            const avgRate = this.calculateAverageRate(activeProfs.length ? activeProfs : allProfessors);
            const stat = (label, value, sub, extra = '') => `<div class="pn-stat ${extra}"><span class="pn-stat-label">${label}</span><span class="pn-stat-value">${value}</span><span class="pn-stat-sub">${sub}</span></div>`;
            const kindColors = { hourly: 'var(--dnr-teal-400)', fixed: 'var(--dnr-teal-700)', freelance: 'var(--dnr-amber-500)' };
            const stats = [
                stat('En alta hoy', `${counts.active} <span class="pl-stat-of">de ${counts.all} fichas</span>`, `${counts.all - counts.active - counts.upcoming} sin contrato en vigor`),
                manage ? `<button type="button" class="pn-stat pl-stat-button ${upcomingUntold ? 'is-warn' : ''}" ${UIEvents.attr("click", function(event,captured){ProfessorsView.setFilter('situation','upcoming');}, [])}>
                    <span class="pn-stat-label">Próximas altas · 15 días</span><span class="pn-stat-value">${counts.upcoming}</span>
                    <span class="pn-stat-sub ${upcomingUntold ? 'is-warn' : ''}">${upcomingUntold ? `${upcomingUntold} sin avisar a la asesoría` : counts.upcoming ? 'Asesoría avisada' : 'Ninguna a la vista'}</span></button>` : '',
                manage ? `<div class="pn-stat"><span class="pn-stat-label">Plantilla por tipo de pago</span>
                    <span class="pl-split" role="img" aria-label="${Object.entries(kinds).map(([k, n]) => `${this.PAY_KIND_LABELS[k]}: ${n}`).join(', ')}">
                        ${Object.entries(kinds).filter(([, n]) => n).map(([k, n]) => `<span style="flex: ${n}; background: ${kindColors[k]}"></span>`).join('')}
                    </span>
                    <span class="pl-split-legend">${Object.entries(kinds).map(([k, n]) => `<span>${this.PAY_KIND_LABELS[k]} <strong>${n}</strong></span>`).join('')}</span></div>`
                    : stat('Fichas', String(counts.all), 'Profesorado y personal'),
                stat('Horas del curso', `${totalHours.toLocaleString('es-ES')} h`, canSeeSalaries && avgRate ? `Tarifa media ${Utils.formatCurrency(avgRate)}/h · por horas` : 'Suma de todas las asignaciones')
            ].filter(Boolean);

            const situations = [['all', 'Todas'], ['active', 'En alta'], ['upcoming', 'Próximas'], ['inactive', 'Sin contrato']];
            const filters = `
                <section class="pn-card pn-toolbar" aria-label="Filtros">
                    <label class="pn-search pl-search">
                        <i class="fas fa-search" aria-hidden="true"></i>
                        <input type="search" id="professor-search" placeholder="Buscar nombre o especialidad" aria-label="Buscar nombre o especialidad" value="${Utils.sanitizeHTML(this.searchTerm)}">
                    </label>
                    <div class="pn-segment pl-situations" role="group" aria-label="Situación">
                        ${situations.map(([key, label]) => `<button type="button" aria-pressed="${this.situation === key}" class="${this.situation === key ? 'is-on' : ''}" ${UIEvents.attr("click", function(event,captured){ProfessorsView.setFilter('situation',String(captured[0]));}, [key])}>${label} · ${counts[key]}</button>`).join('')}
                    </div>
                    ${manage ? `<select class="pn-select" aria-label="Tipo de pago" ${UIEvents.attr("change", function(event,captured){ProfessorsView.setFilter('payFilter',this.value);}, [])}>
                        <option value="all">Todos los tipos de pago</option>
                        ${Object.entries(this.PAY_KIND_LABELS).map(([k, label]) => `<option value="${k}" ${this.payFilter === k ? 'selected' : ''}>${label}</option>`).join('')}
                    </select>
                    <label class="pn-check"><input type="checkbox" ${this.onlyPending ? 'checked' : ''} ${UIEvents.attr("change", function(event,captured){ProfessorsView.setFilter('onlyPending',this.checked);}, [])}> Solo con algo pendiente</label>` : ''}
                    <span class="pl-chip-slot">${this.pendingKind ? `<button type="button" class="pl-chip" ${UIEvents.attr("click", function(event,captured){ProfessorsView.setFilter('pendingKind',null);}, [])}>${Utils.sanitizeHTML(this.PENDING[this.pendingKind].title)} <i class="fas fa-xmark" aria-hidden="true"></i><span class="sr-only">Quitar filtro</span></button>` : ''}</span>
                </section>`;

            const listed = this.listedProfessors(allProfessors);
            // Staff accounts without a ficha only belong to the unfiltered list.
            const extra = this.situation === 'all' && this.payFilter === 'all' && !this.onlyPending && !this.pendingKind ? this.getExtraStaffAccounts() : [];
            const rows = [...listed, ...extra];
            const title = `${{ active: 'En alta', upcoming: 'Próximas altas', inactive: 'Sin contrato en vigor', all: 'Todas las fichas' }[this.situation]} · ${rows.length} ${rows.length === 1 ? 'persona' : 'personas'}`;

            return `
                <div class="view-container dnr-proposal pn-stack" id="professors-page">
                    ${header}
                    <div class="pl-body pn-stack">
                    <div class="pn-stats pl-stats">${stats.join('')}</div>
                    ${filters}
                    <div class="pn-upload-layout ${manage ? 'pl-layout' : ''}">
                        <div class="pn-main pl-main">
                            <section class="pn-card pn-table-card pl-table-card" aria-labelledby="pl-list-title">
                                <div class="pn-table-head">
                                    <h2 class="pn-title" id="pl-list-title">${Utils.sanitizeHTML(title)}</h2>
                                    <span class="pn-hint">Pulsa un nombre para abrir su ficha</span>
                                </div>
                                <div class="pn-scroll pl-scroll">
                                    <table class="pn-table pl-table">
                                        <thead><tr>
                                            ${this.renderSortableHeader('Persona', 'name')}
                                            ${canSeeSalaries ? this.renderSortableHeader('Pago', 'rate') : ''}
                                            ${this.renderSortableHeader('Contrato', 'hireDate')}
                                            ${this.renderSortableHeader('Carga', 'hours')}
                                            ${manage ? '<th scope="col">Avisos</th>' : ''}
                                            <th scope="col" class="pn-num">Acciones</th>
                                        </tr></thead>
                                        <tbody>${this.renderTableBody(this.pageRows(rows), canSeeSalaries)}</tbody>
                                    </table>
                                </div>
                                <nav class="pl-pager" id="pl-pager" aria-label="Páginas de la lista">${this.renderPager(rows.length)}</nav>
                            </section>
                        </div>
                        ${manage ? this.renderPendingAside(allProfessors) : ''}
                    </div>
                    </div>
                </div>`;
        } catch (e) {
            console.error('Error rendering ProfessorsView:', e);
            return `
                <div class="view-container">
                    <div class="card" style="border-left: 4px solid var(--danger-color); padding: 2rem;">
                        <h2 style="color: var(--danger-color)">Error al cargar profesores</h2>
                        <p>Se ha producido un error al procesar los datos.</p>
                        <pre style="background: #eee; padding: 1rem; margin-top: 1rem; border-radius: 4px;">${Utils.sanitizeHTML(e.message)}</pre>
                        <button class="btn btn-secondary" style="margin-top: 1rem;" ${UIEvents.attr("click", function(event,captured){window.location.reload();}, [])}>
                            Recargar Página
                        </button>
                    </div>
                </div>
            `;
        }
    },

    /** «Pendiente · N» (each kind with its people and a way to deal with them) and the next 30 days. */
    renderPendingAside(allProfessors) {
        const groups = Object.keys(this.PENDING).map(kind => ({ kind, people: allProfessors.filter(p => this.pendingOf(p).includes(kind)) })).filter(g => g.people.length);
        const total = groups.reduce((s, g) => s + g.people.length, 0);
        const names = people => { const list = people.map(p => `${p.firstName} ${p.lastName}`.trim()); return list.length > 3 ? `${list.slice(0, 3).join(' · ')} y ${list.length - 3} más` : list.join(' · '); };
        const button = (label, fn, args = []) => `<button type="button" class="pl-group-btn" ${UIEvents.attr("click", fn, args)}>${label}</button>`;
        const actions = g => {
            const show = button(this.pendingKind === g.kind ? 'Mostrando en la lista' : 'Ver en la lista', function(event,captured){ProfessorsView.setFilter('pendingKind',String(captured[0]));}, [g.kind]);
            if (g.kind === 'advisory') return button(g.people.length === 1 ? 'Avisar a la asesoría' : `Avisar de las ${g.people.length}`, function(event,captured){ProfessorsView.notifyAllAdvisory(this);}) + show;
            return show;
        };

        // Hires and ends of contract in the next 30 days.
        const today = this.todayIso(), until = this.addDays(today, 30);
        const fmt = iso => new Date(`${iso}T12:00:00`).toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });
        const moves = allProfessors.flatMap(p => StaffPay.contractsOf(p).flatMap(c => [
            c.startDate >= today && c.startDate <= until ? { p, date: c.startDate, kind: c.freelance ? 'Alta · autónomo' : c.advisoryNotified ? 'Alta · asesoría avisada' : 'Alta · asesoría sin avisar', warn: !c.freelance && !c.advisoryNotified } : null,
            c.endDate && c.endDate >= today && c.endDate <= until ? { p, date: c.endDate, kind: 'Baja · fin de contrato' } : null
        ].filter(Boolean))).sort((a, b) => a.date.localeCompare(b.date));

        return `<aside class="pn-aside pl-aside">
            <section class="pn-card pn-pad" aria-labelledby="pl-todo-title">
                <div class="pn-row-between">
                    <h2 class="pn-title pn-title-sm" id="pl-todo-title">${total ? `Pendiente · ${total}` : 'Todo al día'}</h2>
                    <span class="pn-mono-note">Hoy</span>
                </div>
                ${groups.length ? groups.map(g => `<div class="pl-group">
                    <div class="pn-row-between"><strong>${this.PENDING[g.kind].title}</strong><span class="pl-count ${this.PENDING[g.kind].warn ? 'is-warn' : ''}">${g.people.length}</span></div>
                    <span class="pn-hint">${Utils.sanitizeHTML(names(g.people))}${this.PENDING[g.kind].note ? ` · ${this.PENDING[g.kind].note}` : ''}</span>
                    <div class="pl-group-actions">${actions(g)}</div>
                </div>`).join('') : '<p class="pn-hint">Nadie tiene avisos, accesos, tarifas ni horarios pendientes.</p>'}
            </section>
            <section class="pn-card pn-pad" aria-labelledby="pl-next-title">
                <h2 class="pn-title pn-title-sm" id="pl-next-title">Próximos 30 días</h2>
                ${moves.length ? `<ul class="pl-moves">${moves.map(m => `<li>
                    <span class="pl-move-date">${fmt(m.date)}</span>
                    <span class="pn-person-text">
                        <button type="button" class="pn-person-name pn-person-link" ${UIEvents.attr("click", function(event,captured){App.navigate('profesor/' + String(captured[0]));}, [m.p.id])}>${Utils.sanitizeHTML(`${m.p.firstName} ${m.p.lastName}`.trim())}</button>
                        <span class="pn-person-kind ${m.warn ? 'pl-warn-text' : ''}">${m.kind}</span>
                    </span>
                </li>`).join('')}</ul>` : '<p class="pn-hint">Ninguna alta ni baja en los próximos 30 días.</p>'}
            </section>
        </aside>`;
    },

    renderTableBody(professors, canSeeSalaries) {
        if (!professors.length) {
            const cols = 4 + (canSeeSalaries ? 1 : 0) + (this.canManageStaff() ? 1 : 0);
            return `<tr><td colspan="${cols}"><div class="dnr-empty pn-empty">${this.searchTerm ? `Nadie coincide con «${Utils.sanitizeHTML(this.searchTerm)}».` : 'Nadie en esta lista con estos filtros.'}</div></td></tr>`;
        }
        return this.renderProfessorRows(professors, canSeeSalaries);
    },

    renderProfessorRows(professors, canSeeSalaries = true) {
        const manage = this.canManageStaff();
        const fmt = iso => new Date(`${String(iso).slice(0, 10)}T12:00:00`).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: '2-digit' });
        const states = { active: ['En alta', 'is-active'], upcoming: ['Próximo', 'is-warning'], inactive: ['Sin contrato', 'is-inactive'] };
        return professors.map(professor => {
            try {
                const isStaff = !!professor.isStaffAccount;
                const first = professor.firstName || (professor.name ? professor.name.split(' ')[0] : 'Sin');
                const last = professor.lastName || (professor.name ? professor.name.split(' ').slice(1).join(' ') : 'Nombre');
                const displayName = `${first} ${last}`.trim();
                const avatar = `<span class="pn-avatar pl-avatar">${professor.photo ? `<img ${StudentPhotos.attributes(professor.photo)} alt="">` : Utils.sanitizeHTML(this.getInitials(first, last))}</span>`;
                const nameHTML = isStaff ? `<span class="pn-person-name">${Utils.sanitizeHTML(displayName)}</span>`
                    : `<button type="button" class="pn-person-name pn-person-link" ${UIEvents.attr("click", function(event,captured){App.navigate("profesor/"+String(captured[0]));}, [professor.id])}>${Utils.sanitizeHTML(displayName)}</button>`;
                const person = `<td><div class="pn-person">${avatar}<span class="pn-person-text">${nameHTML}<span class="pn-person-kind">${Utils.sanitizeHTML(professor.specialty || (isStaff ? '' : 'General'))}</span></span></div></td>`;
                const currentRole = isStaff ? professor.staffRole : (AuthManager.getUser(professor.id)?.role || 'professor');
                if (isStaff) {
                    return `<tr>${person}${canSeeSalaries ? '<td>—</td>' : ''}<td><span class="pn-person-kind">Cuenta sin ficha</span></td><td>—</td>${manage ? '<td></td>' : ''}
                        <td class="pn-num"><div class="pn-actions">
                            <span class="pn-tag">${Utils.sanitizeHTML(this.STAFF_ROLE_LABELS[currentRole] || currentRole)}</span>
                            ${AuthManager.isAdmin() ? `<button type="button" class="pn-icon-btn" aria-label="Eliminar cuenta" title="Eliminar cuenta" ${UIEvents.attr("click", function(event,captured){ProfessorsView.deleteStaffAccount(String(captured[0]),String(captured[1]),String(captured[2]));}, [professor.staffUserId, professor.staffUsername, displayName])}><i class="fas fa-trash"></i></button>` : ''}
                        </div></td></tr>`;
                }
                const kind = this.payKindOf(professor);
                const pay = kind === 'fixed' ? `${Utils.formatCurrency(StaffPay.monthlySalary(professor))}/mes` : `${Utils.formatCurrency(Number(professor.hourlyRate) || 0)}/h`;
                const payKind = { hourly: 'Por horas · bruta', fixed: 'Sueldo fijo · neto', freelance: 'Autónomo · factura' }[kind];
                const { state } = this.getProfessorActiveState(professor);
                const period = professor.hireDate ? (professor.terminationDate ? `${fmt(professor.hireDate)} – ${fmt(professor.terminationDate)}` : `desde ${fmt(professor.hireDate)}`) : 'sin fechas';
                const hours = this.getProfessorHours(professor.id), weekly = this.getProfessorWeeklyHoursNow(professor.id);
                const workload = this.calculateWorkload(weekly);
                const flags = this.pendingOf(professor);
                const hasAccount = !AuthManager.isAdmin() || !!AuthManager.getUser(professor.id);
                const accessButton = AuthManager.isAdmin() && !hasAccount
                    ? `<button type="button" class="pn-icon-btn pl-access" title="Sin acceso: esta ficha aún no tiene cuenta para entrar en SICAF. Pulsa para crear su acceso." ${UIEvents.attr("click", function(event,captured){ProfessorsView.openCreateAccess(String(captured[0]));}, [professor.id])}>${this.ICONS.key}<span class="sr-only">Sin acceso</span></button>` : '';
                const menu = AuthManager.isAdmin() && hasAccount ? `<details class="pn-more">
                    <summary class="pn-icon-btn" aria-label="Acceso y rol" title="Acceso y rol">${this.ICONS.key}</summary>
                    <div class="pn-more-menu pl-role-menu">
                        <label class="pn-field">Rol
                            <select class="pn-select" ${UIEvents.attr("change", function(event,captured){ProfessorsView.handleRoleChange(String(captured[0]),this.value);}, [professor.id])}>
                                ${[['professor', 'Profesor'], ['coordinator', 'Coordinador'], ['rrhh', 'RRHH'], ['admin', 'Admin']].map(([v, l]) => `<option value="${v}" ${currentRole === v ? 'selected' : ''}>${l}</option>`).join('')}
                            </select>
                        </label>
                        ${currentRole === 'professor' && professor.email ? `<button type="button" ${UIEvents.attr("click", function(event,captured){this.closest('details').open=false;ProfessorsView.resendProfessorAccess(String(captured[0]));}, [professor.id])}><i class="fas fa-paper-plane"></i> Enviar acceso por correo</button>` : ''}
                    </div>
                </details>` : '';
                return `<tr>
                    ${person}
                    ${canSeeSalaries ? `<td><span class="pn-person-text"><span class="pl-mono">${pay}</span><span class="pn-person-kind">${payKind}</span></span></td>` : ''}
                    <td><span class="pl-contract"><span class="professor-active-pill ${states[state][1]}">${states[state][0]}</span><span class="pn-person-kind">${period}</span></span></td>
                    <td><span class="pn-person-text"><span class="pl-load-text"><strong class="pl-mono">${hours} h</strong> <span class="pn-person-kind">· ${weekly ? `${String(weekly).replace('.', ',')} h/sem` : 'sin clases'}</span></span>
                        <span class="pl-load" title="${workload.percentage}% de 32 h/semana"><span class="pl-load-fill ${workload.level}" style="width: ${workload.percentage}%"></span></span></span></td>
                    ${manage ? `<td><span class="pl-flags">${flags.length ? flags.map(f => `<span class="pl-flag ${this.PENDING[f].warn ? 'is-warn' : 'is-alert'}">${this.PENDING[f].flag}</span>`).join('') : '<span class="pl-flag">Al día</span>'}</span></td>` : ''}
                    <td class="pn-num"><div class="pn-actions">
                        ${manage && this.situation === 'upcoming' ? this.renderAdvisoryAction(professor) : ''}
                        ${AuthManager.canEditProfessorDirectory() ? `<button type="button" class="pn-icon-btn" aria-label="Editar ficha de ${Utils.sanitizeHTML(displayName)}" title="Editar" ${UIEvents.attr("click", function(event,captured){ProfessorsView.editProfessor(String(captured[0]));}, [professor.id])}>${this.ICONS.edit}</button>` : ''}
                        ${accessButton}
                        ${menu}
                        <details class="pn-more">
                            <summary class="pn-icon-btn" aria-label="Más acciones" title="Más">${this.ICONS.more}</summary>
                            <div class="pn-more-menu">
                                <button type="button" ${UIEvents.attr("click", function(event,captured){this.closest('details').open=false;App.navigate('profesor/' + String(captured[0]));}, [professor.id])}><i class="fas fa-id-card"></i> Abrir ficha</button>
                                ${manage ? `<button type="button" ${UIEvents.attr("click", function(event,captured){this.closest('details').open=false;ProfessorProfile.openContractForm(String(captured[0]),'');}, [professor.id])}><i class="fas fa-file-circle-plus"></i> Nuevo contrato</button>` : ''}
                                ${typeof ContractDocs !== 'undefined' && ContractDocs.canManage() ? `<button type="button" ${UIEvents.attr("click", function(event,captured){this.closest('details').open=false;ContractDocs.pick(String(captured[0]));}, [professor.id])}><i class="fas fa-file-arrow-up"></i> Subir contrato (PDF)</button>` : ''}
                            </div>
                        </details>
                    </div></td>
                </tr>`;
            } catch (err) {
                console.error('Error rendering row for professor:', professor, err);
                return `<tr><td colspan="5" class="pl-row-error">Error mostrando profesor ${Utils.sanitizeHTML(professor.id)}</td></tr>`;
            }
        }).join('');
    },

    /**
     * Contracts whose hire still has no notice to the advisory: recent (60 days) or coming, and
     * not freelance. [{ professor, contract }].
     */
    pendingAdvisoryNotices(professors) {
        const today = this.todayIso(), since = this.addDays(today, -60);
        return professors.map(professor => ({ professor, contract: StaffPay.focusContract(professor) }))
            .filter(({ contract }) => contract?.id && !contract.freelance && !contract.advisoryNotified && contract.startDate >= since && (!contract.endDate || contract.endDate >= today));
    },

    renderAdvisoryAction(professor) {
        const contract = StaffPay.focusContract(professor);
        if (!contract?.id) return '';
        if (contract.freelance) return '<span class="professor-active-pill is-inactive professor-pill-plain" title="Autónomo: no se da de alta en la asesoría">Autónomo</span>';
        if (contract.advisoryNotified) return `<button type="button" class="professor-active-pill is-active professor-pill-button" title="El alta ya se envió a la asesoría. Pulsa para dejarla como no avisada."
                ${UIEvents.attr("click", function(event,captured){ProfessorsView.unmarkAdvisory(String(captured[0]),String(captured[1]));}, [professor.id, contract.id])}>Avisada</button>`;
        return `<button type="button" class="btn btn-primary btn-sm" title="Avisar a la asesoría: enviarle el alta de este contrato"
                ${UIEvents.attr("click", function(event,captured){ProfessorsView.notifyAdvisory(String(captured[0]),String(captured[1]),this);}, [professor.id, contract.id])}>
            <i class="fas fa-paper-plane"></i> Avisar</button>`;
    },

    async notifyAdvisory(professorId, contractId, button) {
        if (button?.disabled) return;
        if (button) button.disabled = true;
        await ProfessorService.sendAdvisoryEmail(professorId, contractId);
        this.refresh();
    },

    /** Undo a notice marked by mistake (or a test): the hire is pending again; nothing is sent. */
    unmarkAdvisory(professorId, contractId) {
        const professor = DataManager.getProfessor(professorId);
        const name = professor ? `${professor.firstName} ${professor.lastName}`.trim() : 'esta persona';
        Utils.showConfirm(`¿Dejar el alta de ${name} como no avisada a la asesoría? No se envía nada; volverá a salir «Avisar».`, async () => {
            try {
                await ProfessorService.updateContract(contractId, { advisoryNotified: false });
                Utils.showToast('Alta marcada como no avisada.', 'success');
            } catch (error) { Utils.showToast(error.message, 'error'); }
            if (document.getElementById('professors-page')) this.refresh();
            else App.navigate(App.currentView, false);
        });
    },

    /** Every hire pending its notice to the advisory, one e-mail each, after one confirmation. */
    notifyAllAdvisory(button) {
        const pending = this.pendingAdvisoryNotices(DataManager.getProfessors() || []);
        if (!pending.length || button?.disabled) return;
        const names = pending.map(({ professor }) => `${professor.firstName} ${professor.lastName}`.trim()).join(', ');
        Utils.showConfirm(`¿Enviar a la asesoría el alta de ${pending.length === 1 ? 'esta persona' : `estas ${pending.length} personas`}? ${names}.`, async () => {
            if (button) button.disabled = true;
            let sent = 0;
            // One by one: each notice is its own e-mail and marks its own contract.
            for (const { professor, contract } of pending) if (await ProfessorService.sendAdvisoryEmail(professor.id, contract.id, { quiet: true })) sent++;
            Utils.showToast(sent === pending.length ? `Asesoría avisada de ${sent} ${sent === 1 ? 'alta' : 'altas'}.`
                : `Avisadas ${sent} de ${pending.length}. Las que faltan siguen pendientes.`, sent === pending.length ? 'success' : 'warning');
            this.refresh();
        });
    },

    /** The rows of the current page (the page is pulled back if the list got shorter). */
    pageRows(rows) {
        const pages = Math.max(1, Math.ceil(rows.length / this.pageSize));
        this.page = Math.min(Math.max(1, this.page), pages);
        return rows.slice((this.page - 1) * this.pageSize, this.page * this.pageSize);
    },

    renderPager(total) {
        const pages = Math.ceil(total / this.pageSize);
        if (pages <= 1) return '';
        const from = (this.page - 1) * this.pageSize + 1, to = Math.min(this.page * this.pageSize, total);
        // First, last and the pages around the current one; the gaps become «…».
        const shown = [...new Set([1, this.page - 1, this.page, this.page + 1, pages])].filter(n => n >= 1 && n <= pages).sort((a, b) => a - b);
        const go = (label, page, extra = '') => `<button type="button" class="pl-page-btn ${extra}" ${page === this.page ? 'aria-current="page"' : ''} ${UIEvents.attr("click", function(event,captured){ProfessorsView.goToPage(Number(captured[0]));}, [page])}>${label}</button>`;
        const numbers = shown.map((n, i) => (i && n - shown[i - 1] > 1 ? '<span class="pl-page-gap">…</span>' : '') + go(n, n, n === this.page ? 'is-on' : '')).join('');
        return `<span class="pl-pager-info"><strong>${from}–${to}</strong> de <strong>${total}</strong></span>
            <span class="pl-pager-pages">
                <button type="button" class="pl-page-btn" aria-label="Página anterior" ${this.page === 1 ? 'disabled' : ''} ${UIEvents.attr("click", function(event,captured){ProfessorsView.goToPage(ProfessorsView.page - 1);}, [])}><i class="fas fa-chevron-left" aria-hidden="true"></i></button>
                ${numbers}
                <button type="button" class="pl-page-btn" aria-label="Página siguiente" ${this.page === pages ? 'disabled' : ''} ${UIEvents.attr("click", function(event,captured){ProfessorsView.goToPage(ProfessorsView.page + 1);}, [])}><i class="fas fa-chevron-right" aria-hidden="true"></i></button>
            </span>`;
    },

    goToPage(page) {
        this.page = page;
        this.updateTable();
    },

    updateTable() {
        const tbody = document.querySelector('#professors-page .pl-table tbody');
        if (!tbody) return this.refresh();
        const canSeeSalaries = (typeof AuthManager !== 'undefined') && AuthManager.isLoggedIn() ? AuthManager.canSeeSalaries() : true;
        const extra = this.situation === 'all' && this.payFilter === 'all' && !this.onlyPending && !this.pendingKind ? this.getExtraStaffAccounts() : [];
        const rows = [...this.listedProfessors(DataManager.getProfessors() || []), ...extra];
        SafeHTML.set(tbody, this.renderTableBody(this.pageRows(rows), canSeeSalaries));
        const pager = document.getElementById('pl-pager');
        if (pager) SafeHTML.set(pager, this.renderPager(rows.length));
        const title = document.getElementById('pl-list-title');
        if (title) title.textContent = title.textContent.replace(/· \d+ personas?$/, `· ${rows.length} ${rows.length === 1 ? 'persona' : 'personas'}`);
    },

    // Account for a ficha that has none. Profesor: needs the ficha's e-mail and receives the
    // username and a temporary password by e-mail. Admin, RRHH, coordination and warehouse:
    // the administrator sets the first password. Either way it is changed on first sign-in.
    openCreateAccess(professorId, role = 'professor') {
        const professor = DataManager.getProfessor(professorId);
        if (!professor || !AuthManager.isAdmin() || AuthManager.getUser(professorId)) return;
        const first = Utils.normalizeString(professor.firstName || '').charAt(0);
        const last = Utils.normalizeString((professor.lastName || '').split(' ')[0]);
        const name = `${professor.firstName} ${professor.lastName}`.trim();
        const email = professor.email || '';
        ModalManager.show('Crear acceso', `
            <div class="dnr-proposal professor-form-dnr">
            <form id="create-access-form" class="form-container">
                <p>Cuenta para que <strong>${Utils.sanitizeHTML(name)}</strong> entre en SICAF. Podrá entrar con el usuario o con el correo de su ficha y deberá cambiar la contraseña la primera vez.</p>
                <div class="form-grid">
                    <div class="form-group">
                        <label class="form-label" for="access-username">Usuario</label>
                        <input type="text" class="form-input" id="access-username" name="accessUsername" required maxlength="128" pattern="[a-zA-Z0-9._@\\-]+" autocapitalize="none" spellcheck="false" value="${Utils.sanitizeHTML(first && last ? `${first}.${last}` : '')}">
                    </div>
                    <div class="form-group">
                        <label class="form-label" for="access-role">Rol</label>
                        <select class="form-input" id="access-role" name="accessRole">
                            ${[['professor', 'Profesor'], ['coordinator', 'Coordinador'], ['rrhh', 'RRHH'], ['warehouse', 'Almacén'], ['admin', 'Admin']].map(([value, label]) =>
                                `<option value="${value}" ${value === role ? 'selected' : ''}>${label}</option>`).join('')}
                        </select>
                    </div>
                    <div class="form-group" id="access-mail-note" style="grid-column: 1 / -1;">
                        ${email
                            ? `<p class="pf-hint"><i class="fas fa-paper-plane"></i> Se enviará a <strong>${Utils.sanitizeHTML(email)}</strong> el usuario, una contraseña temporal y el enlace para entrar.</p>`
                            : '<p class="pf-hint"><i class="fas fa-triangle-exclamation"></i> Esta ficha no tiene correo. Para darle acceso como profesor, añade antes su correo en la ficha.</p>'}
                    </div>
                    <div class="form-group" id="access-password-group" style="grid-column: 1 / -1;">
                        <label class="form-label" for="access-password">Contraseña inicial</label>
                        <input type="password" class="form-input" id="access-password" name="accessPassword" minlength="10" autocomplete="new-password" placeholder="Mínimo 10 caracteres">
                    </div>
                </div>
                <div class="form-actions">
                    <button type="button" class="btn btn-secondary" ${UIEvents.attr("click", function(event,captured){ModalManager.closeModal();}, [])}>Cancelar</button>
                    <button type="submit" class="btn btn-primary"><i class="fas fa-key"></i> Crear acceso</button>
                </div>
            </form>
            </div>`, [], () => this.updateTable());
        const form = document.getElementById('create-access-form');
        const button = form.querySelector('[type="submit"]');
        const byMail = () => form.elements.accessRole.value === 'professor';
        const sync = () => {
            const mail = byMail();
            form.querySelector('#access-mail-note').style.display = mail ? '' : 'none';
            form.querySelector('#access-password-group').style.display = mail ? 'none' : '';
            form.elements.accessPassword.required = !mail;
            button.disabled = mail && !email;
            SafeHTML.set(button, mail ? '<i class="fas fa-paper-plane"></i> Crear y enviar acceso' : '<i class="fas fa-key"></i> Crear acceso');
        };
        form.elements.accessRole.addEventListener('change', sync);
        sync();
        form.addEventListener('submit', async event => {
            event.preventDefault();
            if (this._creatingAccess) return;
            this._creatingAccess = true;
            button.disabled = true;
            const username = form.elements.accessUsername.value.trim();
            try {
                if (byMail()) {
                    const result = await AuthManager.sendProfessorAccess(professorId, username);
                    if (!result.success) throw new Error(result.message || 'No se pudo crear el acceso.');
                    Utils.showToast(result.sent ? `Acceso creado (@${result.user.username}) y enviado a ${email}.`
                        : `Acceso creado (@${result.user.username}), pero el correo no se pudo enviar. Vuelve a enviarlo desde el listado.`, result.sent ? 'success' : 'warning');
                } else {
                    const result = await AuthManager.createUser({ username, password: form.elements.accessPassword.value,
                        role: form.elements.accessRole.value, name, professorId });
                    if (!result.success) throw new Error(result.message || 'No se pudo crear el acceso.');
                    Utils.showToast(`Acceso creado: @${result.user?.username || username}`, 'success');
                }
                ModalManager.closeModal();
            } catch (error) {
                Utils.showToast(error.message, 'error');
            } finally {
                this._creatingAccess = false;
                if (document.body.contains(button)) sync();
            }
        });
        form.elements.accessUsername.focus();
    },

    // New temporary password by e-mail for a professor who already has an account.
    async resendProfessorAccess(professorId) {
        const professor = DataManager.getProfessor(professorId);
        if (!professor || !AuthManager.isAdmin() || this._creatingAccess) return;
        if (!professor.email) { Utils.showToast('Esta ficha no tiene correo. Añádelo antes de enviar el acceso.', 'warning'); return; }
        const choice = await SafeHTML.fire({
            title: `¿Enviar acceso a ${professor.firstName}?`,
            html: `Recibirá en <b>${Utils.sanitizeHTML(professor.email)}</b> su usuario, una <b>contraseña nueva</b> y el enlace para entrar. La contraseña anterior dejará de valer.`,
            icon: 'question', showCancelButton: true, confirmButtonColor: '#0d9488', cancelButtonColor: '#64748b',
            confirmButtonText: 'Enviar', cancelButtonText: 'Cancelar'
        });
        if (!choice.isConfirmed) return;
        this._creatingAccess = true;
        try {
            const result = await AuthManager.sendProfessorAccess(professorId);
            if (!result.success) throw new Error(result.message || 'No se pudo enviar el acceso.');
            Utils.showToast(result.sent ? `Acceso enviado a ${professor.email}.` : 'No se pudo enviar el correo. Vuelve a intentarlo.', result.sent ? 'success' : 'warning');
        } catch (error) {
            Utils.showToast(error.message, 'error');
        } finally {
            this._creatingAccess = false;
        }
    },

    async handleRoleChange(professorId, newRole) {
        // Without an account the role is chosen while creating it.
        if (AuthManager.isAdmin() && !AuthManager.getUser(professorId)) return newRole && this.openCreateAccess(professorId, newRole);
        if (typeof AuthManager !== 'undefined') {
            const result = await AuthManager.updateUserRole(professorId, newRole);
            if (result.success) {
                Utils.showToast(`Rol actualizado a ${newRole}`, 'success');
            } else {
                Utils.showToast('Error actualizando rol: ' + result.message, 'error');
                this.updateTable(); // The selector goes back to the real role.
            }
        }
    },

    getInitials(firstName, lastName) {
        try {
            const first = firstName || '?';
            const last = lastName || '?';
            return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();
        } catch {
            return '??';
        }
    },

    // Curso académico actual (Oct de yearStart a Sep de yearStart+1), mismo criterio que professors/profile.js
    getCurrentAcademicYearRange() {
        const today = new Date();
        const yearStart = today.getMonth() >= 8 ? today.getFullYear() : today.getFullYear() - 1;
        const start = new Date(yearStart, 9, 1); // 1 Oct
        const end = new Date(yearStart + 1, 8, 30); // 30 Sep
        const pad = n => String(n).padStart(2, '0');
        const toStr = d => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
        // getProfessorAverageWeeklyHoursInPeriod compares its endDate arg directly
        // against a Date in a loop condition, so it only works with Date objects,
        // not strings (unlike getProfessorTotalHoursInPeriod, which wraps its args
        // in `new Date(...)` internally) — expose both forms so callers can't get it wrong.
        return { startStr: toStr(start), endStr: toStr(end), startDate: start, endDate: end };
    },

    getProfessorHours(professorId) {
        try {
            const { startStr, endStr } = this.getCurrentAcademicYearRange();
            const hours = ProfessorService.getProfessorTotalHoursInPeriod(professorId, startStr, endStr);
            return Math.round(hours);
        } catch (e) {
            console.error('Error calculating hours', e);
            return 0;
        }
    },

    // Media de horas semanales durante el curso académico actual (solo semanas con clase)
    getProfessorWeeklyHoursNow(professorId) {
        try {
            const { startDate, endDate } = this.getCurrentAcademicYearRange();
            const weeklyHours = ProfessorService.getProfessorAverageWeeklyHoursInPeriod(professorId, startDate, endDate);
            return Math.round(weeklyHours * 10) / 10;
        } catch (e) {
            console.error('Error calculating weekly hours', e);
            return 0;
        }
    },

    calculateWorkload(weeklyHours) {
        const maxWeeklyHours = 32; // 100% = 32h/semana
        const percentage = Math.min(Math.round((weeklyHours / maxWeeklyHours) * 100), 100);

        let level = 'low';
        if (percentage >= 90) level = 'critical';
        else if (percentage >= 70) level = 'high';
        else if (percentage >= 50) level = 'medium';

        return { percentage, level };
    },

    calculateTotalHours(professors) {
        if (!professors) return 0;
        return professors.reduce((total, prof) => total + this.getProfessorHours(prof.id), 0);
    },

    calculateAverageRate(professors) {
        if (!professors || professors.length === 0) return 0;
        // Fixed salaries have no hourly rate and are left out of the average.
        const hourly = professors.filter(prof => !StaffPay.isFixed(prof));
        if (!hourly.length) return 0;
        return hourly.reduce((sum, prof) => sum + (Number(prof.hourlyRate) || 0), 0) / hourly.length;
    },

    openNewProfessorModal() {
        if (!AuthManager.canEditProfessorDirectory()) return;
        if (typeof ProfessorProfile !== 'undefined') {
            const content = ProfessorProfile.renderForm();
            ModalManager.show('Nueva persona', content);
            ProfessorProfile.togglePayType(document.getElementById('professor-form'));
        } else {
            alert('El módulo ProfessorProfile no está cargado.');
        }
    },

    editProfessor(professorId) {
        if (!AuthManager.canEditProfessorDirectory()) return;
        const professor = DataManager.getProfessor(professorId);
        if (professor && typeof ProfessorProfile !== 'undefined') {
            const content = ProfessorProfile.renderForm(professor);
            ModalManager.show('Editar ficha', content);
            ProfessorProfile.togglePayType(document.getElementById('professor-form'));
            if (AuthManager.isAdmin()) StaffModuleAccess.mount(professorId);
        }
    },

    async saveProfessor(e) {
        e.preventDefault();
        if (!AuthManager.canEditProfessorDirectory()) return;
        if (this._savingProfessor) return;
        if (typeof StaffModuleAccess !== 'undefined' && StaffModuleAccess.hasPending()) {
            Utils.showToast('Pulsa «Guardar accesos» antes de guardar la ficha.','warning');
            document.getElementById('staff-access-save')?.focus();
            return;
        }
        this._savingProfessor = true;
        try {
        const formData = new FormData(e.target);
        const professorId = formData.get('professorId');


        const professorData = {
            firstName: formData.get('firstName'),
            lastName: formData.get('lastName'),
            email: formData.get('email'),
            phone: formData.get('phone'),
            dni: formData.get('dni'),
            ssNumber: formData.get('ssNumber'),
            address: formData.get('address'),
            bankAccount: formData.get('bankAccount'),
            specialty: formData.get('specialty'),
            active: formData.get('active') === 'on'
        };
        // Only a new ficha has its dates here (its first contract); later ones go in «Contratos».
        if (e.target.elements.hireDate) Object.assign(professorData, { hireDate: formData.get('hireDate') || '', terminationDate: formData.get('terminationDate') || '' });
        // Only the HR section (admin/RRHH) carries the pay fields.
        if (e.target.elements.payType) Object.assign(professorData, ProfessorProfile.readPayFields(e.target));

        if (professorId) {
            await DataManager.updateProfessor(professorId, professorData);

            // Password change for an existing account (accounts are created from the list).
            const password = formData.get('userPassword');
            if (password && typeof AuthManager !== 'undefined') {
                let result = await AuthManager.updateUserPassword(professorId, password, true);
                if (result.success) {
                    Utils.showToast('Profesor y contraseña actualizados', 'success');
                } else if (result.notFound) {
                    // Accounts are created from the list («Sin acceso»): a professor's by e-mail.
                    Utils.showToast('Profesor actualizado. Aún no tiene cuenta: dale acceso desde Personal («Sin acceso»).', 'info');
                } else {
                    Utils.showToast(result.message || 'No se pudo cambiar la contraseña', 'error');
                    return;
                }
            } else {
                Utils.showToast('Profesor actualizado correctamente', 'success');
            }
        } else {
            // Create Professor
            const newProfessor = await DataManager.createProfessor(professorData);
            e.target.elements.professorId.value = newProfessor.id;
            Utils.showToast('Profesor creado. Para que entre en SICAF, dale acceso desde Personal («Sin acceso»).', 'success');
        }

        // A photo chosen in the form is uploaded once the ficha exists (new or edited).
        const pendingPhoto = typeof ProfessorProfile !== 'undefined' ? ProfessorProfile._pendingPhoto : null;
        if (pendingPhoto) {
            try {
                await ProfessorProfile.uploadPhotoFile(e.target.elements.professorId.value, pendingPhoto);
                ProfessorProfile._pendingPhoto = null;
            } catch (photoError) {
                Utils.showToast(`Ficha guardada, pero la foto no: ${photoError.message}`, 'warning');
            }
        }

        ModalManager.closeModal();

        // Refresh whatever view we are currently on to show updated data (the list, only its parts)
        if (document.getElementById('professors-page')) this.refresh();
        else if (App.currentView) App.navigate(App.currentView, false);
        } catch (error) {
            Utils.showToast(error.message || 'No se pudo guardar la ficha.', 'error');
        } finally {
            this._savingProfessor = false;
        }
    },

    attachEventListeners() {
        const searchInput = document.getElementById('professor-search');
        if (searchInput) {
            // Restore focus and cursor position if re-rendering (simple attempt)
            if (this.searchTerm && searchInput.value !== this.searchTerm) {
                searchInput.value = this.searchTerm;
            }

            searchInput.addEventListener('input', (e) => {
                this.searchTerm = e.target.value;
                this.page = 1;
                this.updateTable();
            });

            // Auto-focus if we just typed (and re-rendered)
            if (this.searchTerm) {
                searchInput.focus();
                // Move cursor to end
                const len = searchInput.value.length;
                searchInput.setSelectionRange(len, len);
            }
        }
    }
};

// Make ProfessorsView globally available
window.ProfessorsView = ProfessorsView;
console.log('ProfessorsView module loaded and ready.');
